import { formatDistanceToNow } from "date-fns";
import { Bot, RotateCcw, X, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRecentJobs, useRetryJob, useDismissJob } from "@/hooks/useAgentJobs";
import { toast } from "sonner";
import type { AgentJob } from "@/lib/types";
import { formatError } from "@/lib/utils";

const statusStyles: Record<string, string> = {
  pending: "border-yellow-500 text-yellow-500",
  claimed: "border-yellow-500 text-yellow-500",
  running: "border-blue-500 text-blue-500 animate-pulse",
  completed: "border-green-500 text-green-500",
  failed: "border-red-500 text-red-500",
  cancelled: "border-muted-foreground text-muted-foreground",
};

function JobActions({ job }: { job: AgentJob }) {
  const retryJob = useRetryJob();
  const dismissJob = useDismissJob();

  const handleRetry = () => {
    retryJob.mutate(job, {
      onSuccess: () => toast.success("Job re-dispatched"),
      onError: (err) => toast.error(formatError(err)),
    });
  };

  const handleDismiss = () => {
    dismissJob.mutate(job.id, {
      onSuccess: () => toast("Job dismissed"),
      onError: (err) => toast.error(formatError(err)),
    });
  };

  if (job.status === "failed") {
    return (
      <div className="flex items-center gap-1 shrink-0">
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 text-[10px] text-primary hover:text-primary"
          onClick={handleRetry}
          disabled={retryJob.isPending}
          title="Retry this job"
        >
          <RotateCcw className="h-3 w-3 mr-0.5" />
          Retry
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-1.5 text-[10px] text-muted-foreground"
          onClick={handleDismiss}
          disabled={dismissJob.isPending}
          title="Dismiss"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    );
  }

  if (job.status === "completed" && job.pr_url) {
    return (
      <a
        href={job.pr_url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-0.5 text-[10px] text-primary hover:underline shrink-0"
      >
        <ExternalLink className="h-3 w-3" />
        PR
      </a>
    );
  }

  if (job.status === "completed" || job.status === "cancelled") {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-1.5 text-[10px] text-muted-foreground shrink-0"
        onClick={() =>
          dismissJob.mutate(job.id, {
            onSuccess: () => toast("Cleared"),
          })
        }
        title="Clear"
      >
        <X className="h-3 w-3" />
      </Button>
    );
  }

  return null;
}

export function AgentFeed() {
  const { data: jobs } = useRecentJobs();

  // Filter out cancelled jobs from the feed
  const visibleJobs = jobs?.filter((j) => j.status !== "cancelled") || [];

  if (visibleJobs.length === 0) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
        <Bot className="h-3.5 w-3.5" />
        Agent Activity
      </h3>
      <div className="space-y-1.5">
        {visibleJobs.slice(0, 8).map((job) => (
          <div
            key={job.id}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm"
          >
            <Badge
              variant="outline"
              className={`text-[10px] shrink-0 ${statusStyles[job.status] ?? ""}`}
            >
              {job.status}
            </Badge>
            <span className="truncate flex-1 text-xs">
              {job.instructions || job.project_tag || "Agent task"}
            </span>
            {job.status === "failed" && job.error && (
              <span
                className="text-[10px] text-red-400 truncate max-w-[160px]"
                title={job.error}
              >
                {job.error.length > 50
                  ? job.error.substring(0, 50) + "..."
                  : job.error}
              </span>
            )}
            <JobActions job={job} />
            <span className="text-[10px] text-muted-foreground shrink-0">
              {formatDistanceToNow(new Date(job.created_at), {
                addSuffix: true,
              })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
